import React from 'react';
import { Paper, Typography, Stack, Box, Skeleton } from '@mui/material';
import { Person, Title, Description, CalendarMonth, AccessTime } from '@mui/icons-material';
import { Appointment as AppointmentResponse } from '@features/appointments';
import { useCreateEncounterFromAppointmentMutation } from '@features/encounters';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  isAppointmentInProgress,
  formatAppointmentTimeStatus,
} from '../utils/dateTimeFormatters';

interface NextAppointmentCardProps {
  appointment?: AppointmentResponse;
  onHover?: (encounterId: string | null) => void;
  showPatientName?: boolean;
  title?: string;
  isLoading?: boolean;
  onAppointmentClick?: (appointment: AppointmentResponse) => void;
}

/**
 * A component to display the next upcoming appointment card
 */
const NextAppointmentCard: React.FC<NextAppointmentCardProps> = ({
  appointment,
  onHover = () => {},
  showPatientName = true,
  title = 'NEXT APPOINTMENT',
  isLoading = false,
  onAppointmentClick,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [createEncounterFromAppointment, { isLoading: isCreatingEncounter }] =
    useCreateEncounterFromAppointmentMutation();

  // Function to count recording inputs
  // const countRecordingInputs = (appointment: AppointmentResponse) => {
  //   return appointment.inputs?.filter((input) => input.type === "recording")
  //     .length;
  // };

  // Handle card click
  const handleCardClick = async () => {
    if (!appointment || isCreatingEncounter) return;

    if (onAppointmentClick) {
      onAppointmentClick(appointment);
      return;
    }

    if (appointment.encounter?.encounter_id) {
      navigate(`/encounters/${appointment.encounter.encounter_id}`);
      return;
    }

    try {
      const newEncounter = await createEncounterFromAppointment({
        appointmentId: appointment.appointment_id,
      }).unwrap();
      navigate(`/encounters/${newEncounter.encounter_id}`);
    } catch (error) {
      enqueueSnackbar('Error creating encounter', { variant: 'error' });
      console.error('Error creating encounter:', error);
    }
  };

  // If loading, show skeleton version
  if (isLoading) {
    return (
      <Paper
        sx={{
          p: 2.5,
          mb: 3,
          backgroundColor: 'primary.soft',
          border: 1,
          borderRadius: '0.75rem',
          borderColor: 'primary.main',
          position: 'relative',
        }}
      >
        {/* Title skeleton */}
        <Skeleton width={120} height={24} sx={{ mb: 2 }} />

        {/* Time badge skeleton */}
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <Skeleton width={80} height={24} sx={{ borderRadius: 2 }} />
        </Box>

        {/* Content skeleton */}
        <Stack spacing={2}>
          {showPatientName && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton width="70%" height={24} />
            </Stack>
          )}

          <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton width="60%" height={24} />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton width="80%" height={24} />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton width="50%" height={24} />
          </Stack>
        </Stack>
      </Paper>
    );
  }

  // If no appointment is provided (and not loading), don't render anything
  if (!appointment) {
    return null;
  }

  return (
    <Paper
      onClick={handleCardClick}
      onMouseEnter={() => onHover(appointment.appointment_id)}
      onMouseLeave={() => onHover(null)}
      sx={{
        p: 2.5,
        mb: 3,
        backgroundColor: 'primary.soft',
        border: 1,
        borderRadius: '0.75rem',
        borderColor: 'primary.main',
        cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2,
        },
      }}
    >
      <Typography
        variant="overline"
        color="primary"
        sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}
      >
        {title}
      </Typography>

      {appointment.start_time && (() => {
        const inProgress = isAppointmentInProgress(appointment.start_time, appointment.duration);
        const timeLabel = formatAppointmentTimeStatus(appointment.start_time, appointment.duration);
        return (
          <Paper
            elevation={0}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              px: 1.5,
              py: 0.5,
              bgcolor: inProgress ? 'success.main' : 'success.soft',
              borderRadius: 2,
              border: 1,
              borderColor: 'success.main',
            }}
          >
            <Typography
              variant="caption"
              color={inProgress ? 'white' : 'success.main'}
              fontWeight={inProgress ? 'bold' : 'medium'}
            >
              {timeLabel}
            </Typography>
          </Paper>
        );
      })()}

      <Stack spacing={1.5}>
        {showPatientName && (
          <Stack direction="row" spacing={1} alignItems="center">
            {appointment.patient ? (
              <Person sx={{ fontSize: 20, color: 'primary.main' }} />
            ) : (
              <Title sx={{ fontSize: 20, color: 'primary.main' }} />
            )}
            <Typography variant="h6">
              {/* {appointment.patient
                ? `${appointment.patient.name}`
                : appointment.encounter || "Untitled Encounter"} */}
              {appointment.patient?.name || appointment.display_title || 'Untitled Appointment'}
            </Typography>
          </Stack>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <Description sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="body1">{appointment.encounter_type || 'No Template'}</Typography>
        </Stack>

        {/* {countRecordingInputs(appointment) > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Mic sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography variant="body1">
              {countRecordingInputs(appointment)} Recording
              {countRecordingInputs(appointment) > 1 ? "s" : ""}
            </Typography>
          </Stack>
        )} */}

        {appointment.start_time && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonth sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body1" fontWeight="medium">
              {format(new Date(appointment.start_time), "EEEE, MMMM d, yyyy 'at' h:mm a")}
            </Typography>
          </Stack>
        )}

        {/* Show duration if available */}
        {appointment.duration && (
          <Stack direction="row" spacing={1} alignItems="center">
            <AccessTime sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body1">
              {`Duration: ${Math.floor(appointment.duration / 60)}h ${appointment.duration % 60}m`}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default NextAppointmentCard;
